import { GrElement, TOKENS_CSS } from "../core/base";
import {
  buildOutputUrl,
  cachedScreenTargets,
  listScreens,
  parseScreenIndex,
  presentNeedsSurface,
  readOutputParam,
  silentScreenTargets,
  type ScreenTarget,
} from "../core/displays";
import { FrameDemand, mirrorSize } from "../core/endpoints";
import {
  captureFrames,
  cast,
  openSerialFrameSink,
  present,
  type CastSession,
  type FrameCapture,
  type PresentSession,
  type SerialFrameSink,
} from "../core/screens";
import { resolvePictureCanvas } from "../core/picture-canvas";
import { ENDPOINT_CSS } from "./endpoint-shared";

/**
 * <gr-output name="stage" label="Program" cast usb>
 *   <canvas width="1600" height="900"></canvas>
 * </gr-output>
 * Where the page's result leaves, as one block — TouchDesigner's Window
 * COMP. The canvas lives *inside* this element — it is the picture
 *           and every destination:
 *             Open display     fills this window (true fullscreen when the
 *                              browser allows it; `f` toggles, Esc returns).
 *                              The bus stays the same, so sliders and
 *                              hardware still drive it.
 *             Send to display  this display → requestFullscreen (no copy).
 *                              Another monitor → same-origin window that
 *                              blits the canvas. WebRTC is only for LAN.
 *             Copy link        `?output=<name>` — the page opens straight
 *                              into this surface (perform mode) on whatever
 *                              machine pastes it.
 *             cast / usb       opt-in Wi-Fi cast and GRF1 USB frames.
 *   out.addEventListener("gr-output-demand", (e) => run(e.detail.active));
 *   out.addEventListener("gr-output-pointer", (e) => pulse(e.detail.x, e.detail.y));
 */

const SCREEN_CSS = `
  :host([data-gr-maximized]) {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    max-width: none;
    max-height: none;
    margin: 0;
    z-index: 2147483000;
    display: flex;
    flex-direction: column;
    border: none;
    border-radius: 0;
    background: #000;
    /* Popover UA sheet uses fit-content; override so the picture fills. */
    overflow: hidden;
  }
  :host([data-gr-maximized]) header,
  :host([data-gr-maximized]) .body { display: none; }
  :host([data-gr-maximized]) .screen {
    flex: 1;
    min-height: 0;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
  }
  :host([data-gr-maximized]) ::slotted(canvas),
  :host([data-gr-maximized]) .mirror {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
  }
  :host([data-gr-maximized]) .tick,
  :host([data-gr-maximized]) .slate,
  :host([data-gr-maximized]) .fps {
    display: none;
  }
  :host([data-gr-cursor="hidden"]) { cursor: none; }
  :host([data-gr-cursor="hidden"]) .exit { opacity: 0; }
  .screen {
    position: relative;
    background: #04060a;
    border-bottom: 1px solid var(--_border);
    line-height: 0;
    touch-action: none;
  }
  :host([preview="controls"]:not([data-gr-maximized])) .screen { display: none; }
  .screen canvas,
  ::slotted(canvas) {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    background: #000;
  }
  .tick {
    position: absolute;
    width: 14px;
    height: 14px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    pointer-events: none;
  }
  .tl { top: 8px; left: 8px; border-right: 0; border-bottom: 0; }
  .tr { top: 8px; right: 8px; border-left: 0; border-bottom: 0; }
  .bl { bottom: 8px; left: 8px; border-right: 0; border-top: 0; }
  .br { bottom: 8px; right: 8px; border-left: 0; border-top: 0; }
  .slate, .fps {
    position: absolute;
    bottom: 10px;
    padding: 4px 8px;
    border: 1px solid var(--_border);
    background: rgba(4, 6, 10, 0.75);
    font-family: var(--_mono);
    font-size: 10px;
    letter-spacing: 0.12em;
    line-height: 1;
    text-transform: uppercase;
    pointer-events: none;
  }
  .slate { left: 10px; color: var(--_accent); }
  .fps { right: 10px; color: var(--_muted); }
  .dests { display: flex; flex-wrap: wrap; gap: 6px; }
  select.display {
    border: 1px solid var(--_border);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: var(--_text);
    font-family: var(--_mono);
    font-size: 10px;
    padding: 6px 8px;
    outline: none;
    max-width: 150px;
  }
  select.display:focus { border-color: var(--_accent); }
  .exit {
    display: none;
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(4, 6, 10, 0.72);
    color: rgba(255, 255, 255, 0.82);
    font: 10px/1 var(--_mono);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 8px 12px;
    cursor: pointer;
    transition: opacity 0.35s ease;
  }
  :host([data-gr-maximized]) .exit { display: block; }
`;

const MIRROR_MAX_WIDTH = 960;
const CURSOR_IDLE_MS = 3000;

/**
 * srcdoc-safe page URL: `location.href` is `about:srcdoc` inside the Studio
 * preview, while `baseURI` inherits the real page.
 */
function pageUrl(): string {
  try {
    return new URL(document.baseURI).toString();
  } catch {
    return window.location.href;
  }
}

export class GrOutput extends GrElement {
  private demand = new FrameDemand((active, reasons) => this.announce(active, reasons));
  private source: HTMLCanvasElement | null = null;
  private mirror: HTMLCanvasElement | null = null;
  private mirrorCtx: CanvasRenderingContext2D | null = null;
  private slate: HTMLElement | null = null;
  private fps: HTMLElement | null = null;
  private openButton: HTMLButtonElement | null = null;
  private presentButton: HTMLButtonElement | null = null;
  private displaySelect: HTMLSelectElement | null = null;
  private presentIsThisWindow = false;
  private targets: ScreenTarget[] = [];
  private state!: HTMLElement;
  private note!: HTMLElement;
  private observer: IntersectionObserver | null = null;
  private frameRequest = 0;
  private frames = 0;
  private fpsAt = 0;
  private lastSize = "";
  private cursorTimer = 0;
  private castSession: CastSession | null = null;
  private presentSession: PresentSession | null = null;
  private capture: FrameCapture | null = null;
  private sink: SerialFrameSink | null = null;
  private home: { parent: Node; next: ChildNode | null } | null = null;
  private relocating = false;

  connectedCallback(): void {
    if (this.shadowRoot) return;
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>${TOKENS_CSS}${ENDPOINT_CSS}${SCREEN_CSS}</style>
      <header>
        <span class="label">output</span>
        <span class="title"></span>
        <span class="state"></span>
      </header>
      <div class="body"></div>
    `;
    this.state = root.querySelector(".state")!;
    root.querySelector(".title")!.textContent = this.attr("label", "Program");

    this.note = document.createElement("p");
    this.note.className = "note";

    this.buildScreen();

    root.querySelector(".body")!.appendChild(this.note);
  }

  override disconnectedCallback(): void {
    if (this.relocating) return;
    super.disconnectedCallback();
    this.setMaximized(false);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.observer?.disconnect();
    this.observer = null;
    this.stopLoop();
    void this.teardown();
  }

  /** Whether any destination still wants frames — read it once at startup. */
  get needsFrames(): boolean {
    return this.demand.active;
  }

  /** Cover this window with the picture, or return to the console. */
  maximize(on: boolean): void {
    this.setMaximized(on);
  }

  private get key(): string {
    return this.attr("name") || this.id || "output";
  }

  private get ownsCanvas(): boolean {
    return this.lightCanvas() !== null && !this.hasAttribute("canvas");
  }

  private lightCanvas(): HTMLCanvasElement | null {
    const found = this.querySelector("canvas");
    return found instanceof HTMLCanvasElement ? found : null;
  }

  // ── Screen flavour ───────────────────────────────────────────────────────

  private buildScreen(): void {
    const body = this.shadowRoot!.querySelector(".body")!;
    const screen = document.createElement("div");
    screen.className = "screen";
    // The parser may not have appended the child canvas when connectedCallback
    // runs. A local picture always gets a slot, including later canvas replacements.
    const owned = !this.hasAttribute("canvas");
    screen.innerHTML = owned
      ? `<slot></slot>
         <span class="tick tl"></span><span class="tick tr"></span>
         <span class="tick bl"></span><span class="tick br"></span>
         <span class="slate"></span><span class="fps">— fps</span>
         <button type="button" class="exit">Esc · console</button>`
      : `<canvas class="mirror" width="16" height="9"></canvas>
         <span class="tick tl"></span><span class="tick tr"></span>
         <span class="tick bl"></span><span class="tick br"></span>
         <span class="slate"></span><span class="fps">— fps</span>
         <button type="button" class="exit">Esc · console</button>`;
    this.shadowRoot!.insertBefore(screen, body);

    this.mirror = screen.querySelector(".mirror");
    this.mirrorCtx = this.mirror?.getContext("2d") ?? null;
    this.slate = screen.querySelector(".slate");
    this.fps = screen.querySelector(".fps");
    this.setState("idle");

    screen.addEventListener("pointerdown", (event) => this.emitPointer(event, screen));
    screen.addEventListener("pointermove", (event) => this.emitPointer(event, screen));
    screen.querySelector(".exit")?.addEventListener("click", () => this.setMaximized(false));

    const dests = document.createElement("div");
    dests.className = "dests";
    const open = this.action("Open display", () => {
      const turningOn = !this.hasAttribute("data-gr-maximized");
      this.setMaximized(turningOn);
      if (turningOn) void this.requestFullscreen().catch(() => {});
    });
    dests.appendChild(open);
    this.openButton = open;

    this.buildDisplayPicker(dests);

    if (this.hasAttribute("cast")) {
      dests.appendChild(this.toggleAction("Cast over Wi-Fi", "Stop cast", (on) => this.doCast(on)));
    }
    if (this.hasAttribute("usb")) {
      dests.appendChild(this.toggleAction("USB panel", "Stop USB", (on) => this.doUsb(on)));
    }
    dests.appendChild(
      this.action("Copy link", () => {
        void navigator.clipboard
          ?.writeText(buildOutputUrl(pageUrl(), this.key))
          .then(() => this.say("Link copied — that page opens straight into this display."))
          .catch(() => this.say("Clipboard blocked."));
      }),
    );
    body.appendChild(dests);

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) this.demand.want("mirror", entry.isIntersecting);
      this.syncLoop();
    });
    this.observer.observe(this);
    this.bus.publish(`$output/${this.key}`, false, { src: "gr-output" });

    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    window.addEventListener("beforeunload", this.onUnload);

    // Perform mode: `?output=<name>` opens the page straight into this
    // surface. The whole page still runs — console hidden, bus live.
    if (readOutputParam(window.location.search) === this.key) {
      const enter = (): void => this.setMaximized(true);
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", enter, { once: true });
      } else {
        enter();
      }
    }
  }

  // ── Display picker ───────────────────────────────────────────────────────

  private buildDisplayPicker(dests: HTMLElement): void {
    const select = document.createElement("select");
    select.className = "display";
    select.setAttribute("aria-label", "Target display");
    this.displaySelect = select;
    this.renderDisplayOptions();
    // Enumerating displays prompts a permission the first time, which needs
    // a gesture — opening the picker is one.
    select.addEventListener("pointerdown", () => {
      if (!cachedScreenTargets()) void this.detectDisplays();
    });
    dests.appendChild(select);
    this.presentButton = this.toggleAction("Send to display", "Stop display", (on) => this.doPresent(on));
    this.presentButton.addEventListener("pointerdown", () => {
      if (!cachedScreenTargets()) void this.detectDisplays();
    });
    dests.appendChild(this.presentButton);

    void silentScreenTargets().then((targets) => {
      if (!targets) return;
      this.targets = targets;
      this.renderDisplayOptions();
    });
  }

  private async detectDisplays(): Promise<void> {
    this.targets = await listScreens();
    this.renderDisplayOptions();
    if (this.targets.length === 1) {
      this.say("One display — Send fills this window. Pick New window only if you want a copy.");
    }
  }

  private renderDisplayOptions(): void {
    const select = this.displaySelect;
    if (!select) return;
    const previous = select.value;
    select.textContent = "";
    select.appendChild(new Option("New window", ""));
    for (const target of this.targets) {
      const tag = target.isCurrent
        ? "this window"
        : target.isPrimary
          ? "primary"
          : null;
      select.appendChild(
        new Option(tag ? `${target.label} (${tag})` : target.label, String(target.index)),
      );
    }
    const preferred = parseScreenIndex(this.getAttribute("screen"));
    const current = this.targets.find((target) => target.isCurrent);
    const wanted =
      previous ||
      (preferred !== null && preferred < this.targets.length
        ? String(preferred)
        : current
          ? String(current.index)
          : "");
    select.value = wanted;
    if (select.value !== wanted) select.value = "";
  }

  private get chosenScreenIndex(): number | undefined {
    const value = this.displaySelect?.value ?? "";
    return value === "" ? undefined : Number(value);
  }

  // ── Perform mode (maximize / fullscreen / cursor) ────────────────────────

  private onUnload = (): void => {
    void this.teardown();
  };

  private onFullscreenChange = (): void => {
    // Browser Esc leaves fullscreen; leaving fullscreen leaves the display.
    if (document.fullscreenElement !== this && this.hasAttribute("data-gr-maximized")) {
      this.setMaximized(false);
    }
  };

  private onMaxKey = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target?.isContentEditable) return;
    if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.setMaximized(false);
      return;
    }
    if (event.key !== "f" && event.key !== "F") return;
    event.preventDefault();
    if (document.fullscreenElement === this) void document.exitFullscreen().catch(() => {});
    else void this.requestFullscreen().catch(() => {});
  };

  private setMaximized(on: boolean): void {
    if (on === this.hasAttribute("data-gr-maximized")) return;
    this.toggleAttribute("data-gr-maximized", on);
    document.documentElement.toggleAttribute("data-gr-output-max", on);
    document.documentElement.style.overflow = on ? "hidden" : "";
    this.demand.want("maximize", on);
    this.syncLoop();
    this.openButton?.toggleAttribute("data-active", on);
    if (this.openButton) this.openButton.textContent = on ? "Exit display" : "Open display";
    if (!on && this.presentIsThisWindow) this.releasePresent();
    this.bus.publish("$output/mode", on ? "perform" : "page", { src: "gr-output" });
    if (on) {
      document.addEventListener("keydown", this.onMaxKey);
      this.bindCursorHide(true);
      this.liftToTopLayer(true);
      this.say("Filling this window — sliders and hardware still drive it. Esc returns, F is fullscreen.");
    } else {
      document.removeEventListener("keydown", this.onMaxKey);
      this.bindCursorHide(false);
      this.liftToTopLayer(false);
      if (document.fullscreenElement === this) void document.exitFullscreen().catch(() => {});
    }
  }

  /**
   * `position:fixed` is trapped by ancestor `transform` / `filter` (aurora
   * stage keeps a matrix from its entrance animation). Popover puts us on
   * the top layer; if that's missing, reparent onto `document.body`.
   */
  private liftToTopLayer(on: boolean): void {
    if (on) {
      try {
        this.setAttribute("popover", "manual");
        if (!this.matches(":popover-open")) this.showPopover();
        return;
      } catch {
        this.removeAttribute("popover");
        this.parkInBody(true);
      }
      return;
    }
    try {
      if (this.matches(":popover-open")) this.hidePopover();
    } catch {
      /* already closed or unsupported */
    }
    this.removeAttribute("popover");
    this.parkInBody(false);
  }

  private parkInBody(on: boolean): void {
    if (on) {
      if (this.home || !this.parentNode || this.parentNode === document.body) return;
      this.home = { parent: this.parentNode, next: this.nextSibling };
      this.relocating = true;
      document.body.appendChild(this);
      this.relocating = false;
      return;
    }
    if (!this.home) return;
    this.relocating = true;
    this.home.parent.insertBefore(this, this.home.next);
    this.relocating = false;
    this.home = null;
  }

  private onCursorMove = (): void => {
    this.removeAttribute("data-gr-cursor");
    this.scheduleCursorHide();
  };

  private bindCursorHide(on: boolean): void {
    if (on && this.attr("cursor") !== "visible") {
      document.addEventListener("mousemove", this.onCursorMove, { passive: true });
      this.scheduleCursorHide();
      return;
    }
    document.removeEventListener("mousemove", this.onCursorMove);
    window.clearTimeout(this.cursorTimer);
    this.removeAttribute("data-gr-cursor");
  }

  private scheduleCursorHide(): void {
    window.clearTimeout(this.cursorTimer);
    this.cursorTimer = window.setTimeout(() => {
      this.setAttribute("data-gr-cursor", "hidden");
    }, CURSOR_IDLE_MS);
  }

  private action(label: string, run: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "act";
    button.textContent = label;
    button.addEventListener("click", run);
    return button;
  }

  private toggleAction(
    idle: string,
    active: string,
    run: (turningOn: boolean) => Promise<void>,
  ): HTMLButtonElement {
    const button = this.action(idle, () => {
      const turningOn = !button.hasAttribute("data-active");
      button.disabled = true;
      void run(turningOn)
        .then(() => {
          button.toggleAttribute("data-active", turningOn);
          button.textContent = turningOn ? active : idle;
        })
        .catch((error: unknown) => {
          this.say(error instanceof Error ? error.message : String(error));
        })
        .finally(() => {
          button.disabled = false;
          this.updateLive();
          this.syncLoop();
        });
    });
    return button;
  }

  private resolveSource(): HTMLCanvasElement | null {
    if (this.source?.isConnected) return this.source;
    const owned = this.lightCanvas();
    if (owned && this.ownsCanvas) {
      this.source = owned;
      return owned;
    }
    const selector = this.attr("canvas");
    const found = selector ? document.querySelector(selector) : owned;
    this.source = resolvePictureCanvas(found);
    return this.source;
  }

  private syncLoop(): void {
    if (this.demand.has("mirror") && !this.frameRequest) {
      this.frameRequest = requestAnimationFrame((now) => this.drawMirror(now));
      return;
    }
    if (!this.demand.has("mirror")) this.stopLoop();
  }

  private stopLoop(): void {
    if (!this.frameRequest) return;
    cancelAnimationFrame(this.frameRequest);
    this.frameRequest = 0;
  }

  private drawMirror(now: number): void {
    this.frameRequest = requestAnimationFrame((next) => this.drawMirror(next));
    const source = this.resolveSource();
    if (!source) return;

    const perform = this.hasAttribute("data-gr-maximized");
    const size = `${source.width}×${source.height}:${perform ? "full" : "preview"}`;
    if (size !== this.lastSize) {
      this.lastSize = size;
      if (this.slate) {
        this.slate.textContent = `${this.attr("label", "program")} · ${source.width}×${source.height}`;
      }
      if (this.mirror && this.mirrorCtx) {
        const scaled = perform
          ? { width: Math.max(1, source.width), height: Math.max(1, source.height) }
          : mirrorSize(source.width, source.height, MIRROR_MAX_WIDTH);
        this.mirror.width = scaled.width;
        this.mirror.height = scaled.height;
      }
    }
    if (this.mirror && this.mirrorCtx) {
      this.mirrorCtx.drawImage(source, 0, 0, this.mirror.width, this.mirror.height);
    }

    this.frames++;
    if (now - this.fpsAt >= 1000) {
      if (this.fps) this.fps.textContent = `${Math.round((this.frames * 1000) / (now - this.fpsAt))} fps`;
      this.frames = 0;
      this.fpsAt = now;
    }
  }

  private emitPointer(event: PointerEvent, screen: HTMLElement): void {
    const rect = screen.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    this.dispatchEvent(
      new CustomEvent("gr-output-pointer", {
        bubbles: true,
        composed: true,
        detail: {
          x: (event.clientX - rect.left) / rect.width,
          y: (event.clientY - rect.top) / rect.height,
          buttons: event.buttons,
        },
      }),
    );
  }

  private announce(active: boolean, reasons: string[]): void {
    this.dispatchEvent(
      new CustomEvent("gr-output-demand", { bubbles: true, detail: { active, reasons } }),
    );
  }

  /**
   * Live means the picture left this window — a visible mirror is still just
   * the console looking at itself, so it doesn't light the block up.
   */
  private updateLive(): void {
    const live = this.castSession !== null || this.presentSession !== null || this.sink !== null;
    if (live === this.hasAttribute("data-live")) return;
    this.toggleAttribute("data-live", live);
    this.bus.publish(`$output/${this.key}`, live, { src: "gr-output" });
  }

  private setState(text: string): void {
    this.state.textContent = text;
  }

  private say(message: string): void {
    this.note.replaceChildren(document.createTextNode(message));
  }

  private sayWithLink(message: string, url: string): void {
    this.say(message);
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = "Open receiver ↗";
    this.note.append(document.createElement("br"), link);
  }

  private requireSource(): HTMLCanvasElement {
    const source = this.resolveSource();
    if (!source) {
      throw new Error(
        this.attr("canvas")
          ? `No canvas matches "${this.attr("canvas")}"`
          : "No <canvas> inside this <gr-output>",
      );
    }
    return source;
  }

  private async doCast(turningOn: boolean): Promise<void> {
    if (!turningOn) {
      await this.castSession?.close();
      this.castSession = null;
      this.demand.want("cast", false);
      this.setState(this.presentSession ? "presenting" : "idle");
      this.say("");
      return;
    }
    const source = this.requireSource();
    this.demand.want("cast", true);
    this.syncLoop();
    try {
      this.castSession = await cast(source, {
        fps: this.numAttr("fps", 30),
        onState: (state, detail) => {
          if (state === "connected") this.setState("casting");
          if (state === "failed") this.say(detail ?? "Cast failed");
        },
      });
    } catch (error) {
      this.demand.want("cast", false);
      throw error;
    }
    this.setState("cast waiting");
    this.sayWithLink("Open this private link on every screen:", this.castSession.receiverUrl);
  }

  private releasePresent(): void {
    this.presentIsThisWindow = false;
    this.presentSession = null;
    this.demand.want("present", false);
    this.presentButton?.toggleAttribute("data-active", false);
    if (this.presentButton) this.presentButton.textContent = "Send to display";
    this.updateLive();
    this.setState(this.castSession ? "casting" : "idle");
  }

  private async doPresent(turningOn: boolean): Promise<void> {
    if (!turningOn) {
      const wasThisWindow = this.presentIsThisWindow;
      this.presentIsThisWindow = false;
      await this.presentSession?.close();
      this.presentSession = null;
      this.demand.want("present", false);
      if (wasThisWindow) this.setMaximized(false);
      this.setState(this.castSession ? "casting" : "idle");
      return;
    }
    const source = this.requireSource();
    this.demand.want("present", true);
    this.syncLoop();
    try {
      if (this.targets.length === 0) await this.detectDisplays();
      if (!presentNeedsSurface(this.targets, this.chosenScreenIndex)) {
        this.presentIsThisWindow = true;
        this.setMaximized(true);
        void this.requestFullscreen().catch(() => {});
        this.presentSession = {
          kind: "local",
          close: async () => {
            this.presentIsThisWindow = false;
            this.setMaximized(false);
          },
        };
        this.setState("this window");
        return;
      }
      this.presentSession = await present(source, {
        fps: this.numAttr("fps", 30),
        label: this.attr("label", this.key),
        windowName: `granrojo-screen-${this.key}`,
        screenIndex: this.chosenScreenIndex,
        onState: (state) => {
          if (state === "closed") this.releasePresent();
        },
      });
    } catch (error) {
      this.demand.want("present", false);
      throw error;
    }
    this.setState("presenting");
  }

  private async doUsb(turningOn: boolean): Promise<void> {
    if (!turningOn) {
      this.capture?.stop();
      this.capture = null;
      await this.sink?.close();
      this.sink = null;
      this.demand.want("usb", false);
      this.setState(this.castSession || this.presentSession ? "casting" : "idle");
      return;
    }
    const source = this.requireSource();
    const width = this.numAttr("usb-width", 320);
    const height = this.numAttr("usb-height", 240);
    const fps = this.numAttr("usb-fps", 8);
    this.demand.want("usb", true);
    this.syncLoop();
    try {
      this.sink = await openSerialFrameSink({ baudRate: this.numAttr("usb-baud", 921600) });
      this.capture = captureFrames(source, { width, height, fps, format: "jpeg", quality: 0.72 }, (frame) =>
        this.sink?.write(frame),
      );
    } catch (error) {
      this.demand.want("usb", false);
      await this.sink?.close().catch(() => {});
      this.sink = null;
      throw error;
    }
    this.setState("usb frames");
    this.say(`GRF1 JPEG packets · ${width}×${height} · up to ${fps} fps`);
  }

  private async teardown(): Promise<void> {
    this.setMaximized(false);
    window.removeEventListener("beforeunload", this.onUnload);
    this.capture?.stop();
    this.capture = null;
    await this.sink?.close().catch(() => {});
    this.sink = null;
    await this.castSession?.close().catch(() => {});
    this.castSession = null;
    await this.presentSession?.close().catch(() => {});
    this.presentSession = null;
  }

}

customElements.define("gr-output", GrOutput);
